import React from 'react';
import { Stack, Button, CssBaseline } from '@mui/material';
import AppTheme from './shared-theme/AppTheme';
import ColorModeSelect from './shared-theme/ColorModeSelect';
import Header from './components/Form/Header';
import CustomStepper from './components/Form/CustomStepper';
import FormStep from './components/Form/FormStep';
import FormContainer from './components/Form/FormContainer';
import { styled } from '@mui/material/styles';
import MuiCard from '@mui/material/Card';
import { db, collection, addDoc } from "./firebaseConfig";
import { AnimatePresence, motion } from 'framer-motion';
import { paketKambing } from './components/Form/Step/Step2Form';
import { validateReferralCode } from './services/apiService';
// Import Firebase


const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  maxWidth: '90%',
  height: '100vh',
  boxSizing: 'border-box',
  padding: theme.spacing(3),
  gap: theme.spacing(2),
  overflowY: 'auto',
  boxShadow: 'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',

  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
    maxWidth: '720px',
  },

  [theme.breakpoints.up('md')]: {
    width: '60%',
  },

  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

export default function MultiStepForm() {
  const [activeStep, setActiveStep] = React.useState(0);
  const [previousStep, setPreviousStep] = React.useState(0);
  const [formData, setFormData] = React.useState<{ [key: string]: string | string[] | undefined }>({});
  const [isSubmitting, setIsSubmitting] = React.useState(false); // New state for submission
  const [sisaDaging, setSisaDaging] = React.useState<number>(0);
  const [phoneNumber, setPhoneNumber] = React.useState<string>('6285700141117'); // Default phone number

  // Read URL parameters and set referal value - run only once on mount
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const refParam = urlParams.get('ref');
    
    const validateAndSetReferral = async () => {
      if (refParam) {
        console.log('Validating referral code:', refParam);
        try {
          const result = await validateReferralCode(refParam) as { referralCode: string; referralName: string; phoneNumber: string };
          const { referralName, phoneNumber: apiPhoneNumber } = result;
          console.log('API response - Referral Name:', referralName, 'Phone:', apiPhoneNumber);
          
          setFormData((prevData) => ({
            ...prevData,
            referal: referralName,
          }));
          
          setPhoneNumber(apiPhoneNumber && String(apiPhoneNumber).trim() !== '' ? apiPhoneNumber : '6285700141117');
        } catch (error) {
          console.error('Error validating referral:', error);
          // Use default values on error
          setFormData((prevData) => ({
            ...prevData,
            referal: 'Call Center',
          }));
          setPhoneNumber('6285700141117');
        }
      } else {
        console.log('No referal parameter - using default: call-center');
        setFormData((prevData) => ({
          ...prevData,
          referal: 'Call Center',
        }));
        setPhoneNumber('6285700141117');
      }
    };
    
    validateAndSetReferral();
  }, []); // Empty dependency array - run only once

  // Monitor referal value changes for debugging
  React.useEffect(() => {
    console.log('formData.referal changed to:', formData.referal);
  }, [formData.referal]);

  // Reset specific fields
  const resetForm = (fieldsToReset: string[]) => {
    if (!Array.isArray(fieldsToReset)) return;
    setFormData((prevData) => {
      const updatedData = { ...prevData };
      fieldsToReset.forEach((field) => {
        // Don't reset referal field
        if (field !== 'referal') {
          updatedData[field] = '';
        }
      });
      return updatedData;
    });
  };

  React.useEffect(() => {
    if (formData.niat === 'aqiqah') {
      resetForm(['pilih_paket', 'paket_catering']);
      setFormData((prevData) => ({
        ...prevData,
        pilih_paket: 'kambing', // Set default ke kambing
      }));
    } else if (formData.niat === 'peringatan') {
      resetForm([
        'birth_place',
        'namaPanggilan',
        'data_kartu',
        'masakan_daging',
        'selectedVegetables',
        'kulit',
        'package_rice',
        'amount_rice',
        'paket_katalog',
        'kkj',
        'jumlah_kulit_dimasak',
        'menu_kulit',
      ]);
    }

    if (formData.pilih_paket === 'nonkambing') {
      resetForm(['namaPanggilan', 'data_kartu']);
    }
  }, [formData.niat, formData.pilih_paket]);

  // Tambahkan logika untuk memulihkan data saat kembali ke step tertentu
  React.useEffect(() => {
    if (activeStep === 2) {
      setFormData((prevData) => ({
        ...prevData,
        pilih_kambing: prevData.pilih_kambing || '',
        jumlah_ekor: prevData.jumlah_ekor || '',
        jumlah_box: prevData.amount_rice || '',
        selectedVegetables: prevData.selectedVegetables || [],
        jumlah_kulit_dimasak: prevData.jumlah_kulit_dimasak || '',
        // Auto-select Paket Katalog if not set
        // paket_katalog: prevData.paket_katalog || (paketKambing.length > 0 ? paketKambing[0].title : ''),
        paket_katalog: prevData.paket_katalog || '',
      }));
    }
  }, [activeStep]);

  // Reset on pilihan kambing change
  React.useEffect(() => {
    if (formData.paket_katalog) {
      // Reset selectedVegetables explicitly
      setFormData((prevData) => ({
        ...prevData,
        selectedVegetables: [], // Reset selectedVegetables to an empty array
      }));
    }
  }, [formData.paket_katalog]);    // React.useEffect(() => {
  //   if (formData.pilih_paket) {
  //     resetForm(['birth_place']);
  //   }
  // }, [formData.pilih_paket]);


  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const steps = ['Data Pesanan', 'Data Anak', 'Pilihan Menu', 'Dokumentasi & Kartu', 'Review'];

  const handleNext = () => {
    setPreviousStep(activeStep);
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setPreviousStep(activeStep);
    setActiveStep((prev) => prev - 1);
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      const formatValue = (key: string, value: any) => {
        if (key === 'selectedVegetables' && Array.isArray(value)) {
          return value.map(item => item?.title || '-').join(', ');
        }
        if (key === 'dateEvent' && value) {
          const [year, month, day] = value.split('-'); // misalnya 2025-04-08
          return `${day}/${month}/${year}`;
        }

        if (Array.isArray(value)) {
          return value.map(item => (typeof item === 'object' ? item.label || JSON.stringify(item) : item)).join(', ');
        }
        return value ? value.toString() : '-';
      };

      const labelMap: { [key: string]: string } = {
        name: 'Nama',
        phone: 'Nomor WA #1',
        phone2: 'Nomor WA #2',
        kecamatan: 'Kecamatan',
        address: 'Alamat',
        referal: 'Referal',
        dateEvent: 'Tanggal Acara',
        timeEvent: 'Waktu Acara',
        timeArrive: 'Waktu Kedatangan',
        niat: 'Peniatan',
        nameNiat: 'Nama Peniatan',
        namaPanggilan: 'Nama Panggilan',
        birth_place: 'Tempat, Tanggal Lahir',
        data_kartu: 'Data Kartu Ucapan',
        paket_katalog: 'Pilihan Paket Katalog',
        paket_catering: 'Pilihan Paket Catering',
        pilih_kambing: 'Pilihan Kambing',
        jumlah_daging: 'Jumlah Potong Daging',
        masakan_daging: 'Masakan Daging',
        selectedVegetables: 'Pilihan Sayur',
        package_rice: 'Jenis Nasi',
        amount_rice: 'Jumlah Box',
        kkj: 'Kepala Kaki Jeroan (KKJ)',
        kulit: 'Kulit',
        menu_kulit: 'Menu Kulit',
        jumlah_kulit_dimasak: 'Jumlah Kulit Dimasak',
        penyembelihan: 'Penyembelihan',
        souvenir: 'Souvenir',
        kartu: 'Pilihan Kartu',
        amount_box_catering: 'Jumlah Box',
        jumlah_ekor: 'Jumlah Ekor',
        jumlah_jantan: 'Jumlah Jantan',
        pakaiFoto: 'Pakai Foto',

      };

      const orderAqiqah = [
        'referal','name', 'phone', 'phone2', 'kecamatan', 'address',// step 1
        'dateEvent', 'timeEvent', 'timeArrive', //step 2
        'niat', 'nameNiat', 'namaPanggilan', 'birth_place', //step 2
        'paket_katalog', 'paket_catering', 'package_rice', 'amount_rice', //step3
        // 'pilih_kambing','jumlah_daging','jumlah_ekor', 'jumlah_jantan', 'masakan_daging', //step4
        'pilih_kambing', 'jumlah_daging', 'jumlah_ekor', 'jumlah_jantan', 'masakan_daging', //step4
        'kkj', 'selectedVegetables',  //step 5
        'kulit', 'menu_kulit', 'jumlah_kulit_dimasak', //step 6
        'penyembelihan', //step 7
        'souvenir', 'kartu', 'pakaiFoto', 'data_kartu', //step 8
      ];

      const orderPeringatan = [
        'referal','name', 'phone', 'phone2', 'kecamatan', 'address',
        'niat', 'nameNiat', 'dateEvent', 'timeEvent', 'timeArrive',
        'paket_katalog', 'paket_catering', 'amount_box_catering', 'souvenir', 'kartu', 'data_kartu'
      ];

      const selectedOrder = formData.pilih_paket === 'kambing' ? orderAqiqah : orderPeringatan;

      let message = formData.pilih_paket === 'nonkambing'
        ? '*PESANAN CATERINGPEDIA*\n\n'
        : '*PESANAN AQIQAHPEDIA*\n\n';

      const opsionalFields = ['dateEvent', 'timeEvent', 'timeArrive', 'nameNiat', 'birth_place'];


      selectedOrder.forEach(key => {
        const value = formData[key];
        const formatted = formatValue(key, value);

        if (formatted && formatted !== '-' && formatted.trim() !== '') {
          message += `*${labelMap[key] || key}:* ${formatted}\n`;

          // Tambahkan baris kosong setelah label tertentu
          if (
            key === 'address' ||
            key === 'timeArrive' ||
            key === 'birth_place' ||
            key === 'selectedVegetables' ||
            key === 'jumlah_kulit_dimasak' ||
            key === 'penyembelihan'
          ) {
            message += `\n`; // Tambahkan baris kosong
          }
          // 👉 Tambahkan baris kosong setelah `kulit` jika `jumlah_kulit_dimasak` kosong
          const jumlahKulitValue = formData['jumlah_kulit_dimasak'];
          if (
            key === 'kulit' && (
              !jumlahKulitValue ||
              (typeof jumlahKulitValue === 'string' && jumlahKulitValue.trim() === '') ||
              (Array.isArray(jumlahKulitValue) && jumlahKulitValue.length === 0)
            )
          ) {
            message += `\n`;
          }

        } else if (opsionalFields.includes(key)) {
          message += `*${labelMap[key] || key}:* Data Menyusul\n`;
          if (
            key === 'timeArrive' ||
            key === 'birth_place'
          ) {
            message += `\n`;
          }

        }

        // if (key === 'jumlah_ekor') {
        //   message += `*Pakai Jantan:* ${value ? 'Ya' : 'Tidak'}\n`;
        // }

        // Sisipkan Sisa Daging setelah Jumlah Box + tambahkan jarak
        // if (key === 'amount_rice') {
        //   message += `*Sisa Daging:* ${sisaDaging > 0 ? sisaDaging + ' potong matang' : 'Tidak ada'}\n\n`;
        // }

        // if (key === 'jumlah_ekor' &&
        //   (typeof formData.paket_katalog === 'string' && formData.paket_katalog.trim() !== 'Sate A') &&
        //   formData.paket_katalog?.trim() !== 'Sate B') {
        //   message += `*Sisa Daging:* ${sisaDaging > 0 ? sisaDaging + ' potong matang' : 'Tidak ada'}\n\n`;
        // }
      });

      const encodedMessage = encodeURIComponent(message);

      const waUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
      window.open(waUrl, '_blank');

    } catch (error) {
      console.error("Gagal membuat pesan WA:", error);
      alert("Terjadi kesalahan saat menyiapkan pesan.");
    } finally {
      setIsSubmitting(false);
    }
  };



  const requiredFields = [
    ['name', 'phone', 'phone2', 'kecamatan', 'address', 'referal'], // Step 0
    ['niat', ...(formData.pilih_paket === 'nonkambing' ? ['pilih_paket'] : []), ...(formData.pilih_paket !== 'nonkambing' ? ['data_kartu'] : [])], // Step 1
    [
      ...(formData.pilih_paket === 'nonkambing'
        ? ['paket_catering', 'amount_box_catering'] // Jika pilih_paket === nonkambing, hanya paket_catering yang wajib
        : [
          ...(typeof formData.paket_katalog === 'string' && (formData.paket_katalog.trim() === 'Sate A' || formData.paket_katalog.trim() === 'Sate B' || formData.paket_katalog.trim() === 'Box Cetak' || formData.paket_katalog.trim() === 'Box Sebar')
            ? [] // Jika paket_katalog adalah Sate A atau Sate B, pilih_kambing dan masakan_daging tidak wajib
            : ['pilih_kambing', 'masakan_daging']
          ),
          'selectedVegetables',
          'kulit',
          'package_rice',
          'amount_rice',
          ...(formData.kulit === 'dimasak' ? ['jumlah_kulit_dimasak', 'menu_kulit'] : []),
        ]
      ),
    ],
    [
      ...(formData.pilih_paket === 'nonkambing'
        ? []
        : (
          (typeof formData.paket_katalog === 'string' &&
            (formData.paket_katalog.trim() === 'Box Cetak' || formData.paket_katalog.trim() === 'Box Sebar'))
            ? ['penyembelihan'] // souvenir & kartu tidak wajib
            : ['penyembelihan', 'kartu']
        )
      ),
    ],
  ];


  const isFormValid = () => {
    // Add your form validation logic here
    // For example, check if all required fields are filled  
    return requiredFields[activeStep].every(field => formData[field] && formData[field] !== '');
  };



  return (
    <AppTheme>
      <CssBaseline enableColorScheme />
      <FormContainer direction="column" justifyContent="space-between" sx={{ width: '100%', overflow: 'hidden' }}>
        <Card variant="outlined">
          <Header />
          <Stack sx={{ width: '100%' }} spacing={4}>
            <CustomStepper activeStep={activeStep} />
          </Stack>
          <AnimatePresence mode="wait">
            <motion.div
              key={activeStep}
              initial={{ rotateY: activeStep > previousStep ? -90 : 90, opacity: 0 }}
              animate={{ rotateY: 0, opacity: 1 }}
              exit={{ rotateY: activeStep > previousStep ? 90 : -90, opacity: 0 }}
              transition={{ duration: 0.5, ease: 'easeInOut' }}
              style={{ perspective: 1000 }}
            >
              <FormStep
                step={activeStep}
                formData={formData}
                handleChange={handleChange}
                resetForm={resetForm}
                sisaDaging={sisaDaging}
                setSisaDaging={setSisaDaging}

              />
            </motion.div>
          </AnimatePresence>
          <Stack direction="row" spacing={2} sx={{ marginTop: 2 }}>
            {activeStep !== 0 && <Button onClick={handleBack}>Kembali</Button>}
            {activeStep < steps.length - 1 ? (
              <Button variant="contained" onClick={handleNext} disabled={!isFormValid()} sx={{
                '&.Mui-disabled': {
                  backgroundColor: '#E0E0E0', // Warna latar belakang saat disabled
                  color: '#B0B0B0', // Warna teks saat disabled
                },
              }}
              >Lanjut</Button>
            ) : (
              <Button variant="contained" onClick={handleSubmit} disabled={isSubmitting}>Kirim</Button> // Disable when submitting
            )}
          </Stack>
        </Card>
      </FormContainer>
    </AppTheme>
  );
}
